import { Flex, StackItem, VStack } from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { GlanceAndText, hasAtAGlance } from 'components/combination/glanceAndText';
import { CookieDeclaration } from 'components/modules/CookieDeclaration';
import { InbuiltForm } from 'components/modules/InbuiltForm';
import { OverviewBlock } from 'components/modules/OverviewBlock';
import { OverviewSpotAutomatic } from 'components/modules/OverviewSpotAutomatic';
import { TextModule } from 'components/modules/TextModule';
import { toTitleCase } from 'helpers/textCase';
import {
  CampaignPageContent,
  CareersAboutPageContent,
  ContentPage as ContentPageModel,
  CustomFormPageContent,
  FixedNavigation,
  InbuiltForm as InbuiltFormModel,
  InsightsPageContent,
  LegalPageContent,
  NewsPageContent,
  OfficePageContent,
  OverviewSpotAutomatic as OverviewSpotAutomaticModel,
  OverviewSpotAutomaticNoTabs,
  ProductPageContent,
  ProjectPageContent,
  SectorPageContent,
  ServiceLinePageContent,
  ServicePageContent,
  SubsectorPageContent,
  TextModule as TextModuleModel,
  UnifyingThemePageContent,
} from 'models';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { useCookies } from 'react-cookie';
// import slugify from 'slugify';
import { withTranslations } from 'store/translations';
import { ContentType, mapper } from 'utils/componentMapper';
import { hrefLink } from 'utils/normalization/hrefLink';

import SchemaMarkup from '../../helpers/schemaMarkup';
import Breadcrumb, { BackLink } from '../base/Breadcrumb';

type ContentPageProps = {
  content: ContentPageModel;
  pageBodyLinks?: any[] | undefined;
  fixedNavModules?: FixedNavigation[];
};

type LinkedItemType =
  | NewsPageContent
  | SectorPageContent
  | ServiceLinePageContent
  | LegalPageContent
  | OfficePageContent
  | ProductPageContent
  | ProjectPageContent
  | ServicePageContent
  | SubsectorPageContent
  | CampaignPageContent
  | InsightsPageContent
  | CustomFormPageContent
  | CareersAboutPageContent
  | UnifyingThemePageContent;

// Helper function to check if page has inbuilt form
export const hasInbuiltForm = (content: ContentPageModel): InbuiltFormModel | undefined => {
  const linkedItem = content.elements.content.linkedItems[0];
  if (!linkedItem?.elements) return undefined;

  // Check if the page content type supports inbuilt forms and has pageContent
  if (!('pageContent' in linkedItem.elements)) return undefined;

  const pageContent = linkedItem.elements.pageContent?.linkedItems;
  if (!pageContent || !Array.isArray(pageContent)) return undefined;

  // Find and return the first inbuilt form
  const inbuiltForm = (pageContent as any[]).find((element: any) => element.system.type === 'inbuilt_form');
  return inbuiltForm as InbuiltFormModel | undefined;
};

export const ContentPage: FC<ContentPageProps> = ({ content, pageBodyLinks, fixedNavModules }) => {
  //Special cases in Content Page
  //Used to determine when is the first time Text module shows, to add At A Glance to its side
  const pageContent = (content.elements.content.linkedItems[0] as LinkedItemType)?.elements.pageContent.linkedItems;
  const firstTextModuleIndex = pageContent?.map((element) => element.system.type).indexOf('text');
  const atAGlanceModel = hasAtAGlance(content);
  const inbuiltFormModel = hasInbuiltForm(content);
  const router = useRouter();
  const translations = withTranslations();
  const [_cookie, setCookie] = useCookies();
  let jsonLdService: string | undefined = undefined;

  const linkedItem = content.elements.content.linkedItems[0];
  const systemType = linkedItem?.system.type;

  switch (systemType) {
    case 'service_page_content':
      jsonLdService = (linkedItem as ServicePageContent).elements.jsonLdMarkupService?.value;
      break;
    case 'sector_page_content':
      jsonLdService = (linkedItem as SectorPageContent).elements.jsonLdMarkupService?.value;
      break;
    case 'service_line_page_content':
      jsonLdService = (linkedItem as ServiceLinePageContent).elements.jsonLdMarkupService?.value;
      break;
    case 'campaign_page_content':
      jsonLdService = (linkedItem as CampaignPageContent).elements.jsonLdMarkupService?.value;
      break;
    case 'subsector_page_content':
      jsonLdService = (linkedItem as SubsectorPageContent).elements.jsonLdMarkupService?.value;
      break;
    // add more as needed
    default:
      jsonLdService = undefined;
      break;
  }

  React.useEffect(() => {
    if (content.elements.tagsSector.value.length > 0) {
      const tagname = translations.sector(content.elements.tagsSector.value[0]?.codename);
      setCookie('breadcrumb_name', tagname);
    }
    if (content.elements.tagsThemeTopic.value.length > 0) {
      const tagname = translations.theme_topic(content.elements.tagsThemeTopic.value[0]?.codename);
      setCookie('breadcrumb_name', tagname);
    }
  }, [router.isReady, translations.isReady]);

  const slug = router.query.slug || router.query.mainSubpage || router.query.subcategory;

  const asPath = router.asPath;
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.ramboll.com/';
  // const fullUrl = `${baseUrl}${router.locale}${asPath}`;
  const breadcrumbOverride = content.elements.partOfCampaignPage.linkedItems.length
    ? ({
        href: hrefLink(
          content.elements.partOfCampaignPage.linkedItems[0],
          (slug as unknown as string)?.split('/'),
          true,
          router.locale,
          translations,
        ),
        label: content.elements.partOfCampaignPage.linkedItems[0].elements.summaryTitle.value,
      } as BackLink)
    : undefined;
  const overviewLink =
    // content.elements.category.value[0]?.codename == 'product' ||
    content.elements.category.value[0]?.codename == 'project' ||
    content.elements.category.value[0]?.codename == 'insight'
      ? ({
          href: `/${translations.multiple(content.elements.category.value[0]?.codename)}`,
          label: toTitleCase(translations.multiple(content.elements.category.value[0]?.codename)),
        } as BackLink)
      : content.elements.category.value[0]?.codename == 'product'
      ? ({
          href: `/${translations.multiple(content.elements.category.value[0]?.codename)}`,
          label: toTitleCase(translations.multiple(content.elements.category.value[0]?.codename)),
        } as BackLink)
      : undefined;

  const isLegalPage = content.elements.content.linkedItems[0]?.system.type === 'legal_page_content';
  // breadcrumbOverride ? setCookie("breadcrumb_path",breadcrumbOverride) : setCookie("breadcrumb_path",router.asPath)
  const cookiesInUseHeadlineHtml =
    isLegalPage &&
    createRichTextHtmlResolver(nodeParser).resolveRichText({
      element: (content.elements.content.linkedItems[0] as LegalPageContent).elements.cookiesInUseHeadline,
    }).html;

  const cleanJsonLd = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(cleanJsonLd).filter((item) => item !== undefined && item !== null);
    }
    if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {};
      Object.entries(obj).forEach(([key, value]) => {
        const cleanedValue = cleanJsonLd(value);
        const isEmpty =
          cleanedValue === '' ||
          cleanedValue === undefined ||
          cleanedValue === null ||
          (typeof cleanedValue === 'object' &&
            !Array.isArray(cleanedValue) &&
            Object.keys(cleanedValue).length === 0) ||
          (Array.isArray(cleanedValue) && cleanedValue.length === 0);

        if (!isEmpty) {
          newObj[key] = cleanedValue;
        }
      });
      return newObj;
    }
    return obj;
  };

  return (
    <>
      <SchemaMarkup
        id={systemType ?? 'page'}
        jsonLd={(() => {
          try {
            if (!jsonLdService) return undefined;
            const parsed = JSON.parse(jsonLdService);
            const localePath = router.locale && router.locale !== 'en' ? `${router.locale}` : '';
            const urlPath = `${localePath}${asPath}`.replace(/^\/+/, '');
            parsed.url = `${baseUrl.replace(/\/$/, '')}/${urlPath}`;
            const cleaned = cleanJsonLd(parsed);
            return JSON.stringify(cleaned);
          } catch (e) {
            console.warn('Invalid jsonLdService JSON', e);
            return jsonLdService;
          }
        })()}
      />

      {/*---Breadcrumb---*/}
      {!content.elements.hero?.linkedItems.length && breadcrumbOverride != undefined && breadcrumbOverride.href !== '' && (
        <Flex m={'40px'}>
          <Breadcrumb color='mountain.base' customLink={breadcrumbOverride} />
        </Flex>
      )}

      {content && (
        <VStack shouldWrapChildren spacing={0}>
          {/* --- Hero ---*/}
          {content.elements.hero.linkedItems.map((element, index) => {
            return (
              <StackItem key={index} display='block'>
                {mapper(content.elements.hero.linkedItems[0].system.type as ContentType) &&
                  React.createElement(mapper(content.elements.hero.linkedItems[0].system.type as ContentType) as any, {
                    model: element,
                    customBreadcrumb: overviewLink,
                  })}
              </StackItem>
            );
          })}
          {/* --- Page Content ---*/}
          {pageContent?.map((element, index) => {
            if (!Object.values<string>(ContentType).includes(element.system.type))
              return (
                <StackItem key={index} backgroundColor={'cyan.20'} textStyle={'desktop.h5'} p='s'>
                  Component {element.system.type} is not implemented yet.
                </StackItem>
              );
            const type = element.system.type as ContentType;
            {
              /* At a Glance Condition */
            }
            if (type === ContentType.text && atAGlanceModel && index === firstTextModuleIndex)
              return (
                <StackItem key={index} display='block'>
                  <GlanceAndText
                    textModuleModel={element as TextModuleModel}
                    atAGlanceModel={atAGlanceModel}
                    pageBodyLinks={pageBodyLinks}
                  />
                </StackItem>
              );

            {
              /* Inbuilt Form Condition */
            }
            if (type === ContentType.inbuilt_form && inbuiltFormModel) {
              console.log('Rendering Inbuilt Form within Content Page');
              return (
                <StackItem key={index} display='block'>
                  <InbuiltForm model={element as InbuiltFormModel} />
                </StackItem>
              );
            }
            {
              /* Overview Block Condition */
            }
            if (type === ContentType.overview || type === ContentType.overview_spot___automatic) {
              if (type === ContentType.overview) {
                return (
                  <StackItem key={index} display='block'>
                    <OverviewBlock
                      model={element as OverviewSpotAutomaticNoTabs}
                      parentPageCodename={content.system.codename}
                    />
                  </StackItem>
                );
              }
              if (type === ContentType.overview_spot___automatic) {
                return (
                  <StackItem key={index} display='block'>
                    <OverviewSpotAutomatic
                      model={element as OverviewSpotAutomaticModel}
                      parentPageCodename={content.system.codename}
                    />
                  </StackItem>
                );
              }
            }

            if (type === ContentType.cookie_bot && cookiesInUseHeadlineHtml) {
              return (
                <StackItem key={index} display='block'>
                  <CookieDeclaration
                    domainGroupId={element.elements.domainGroupId.value}
                    cookiesInUseHeadlineHtml={cookiesInUseHeadlineHtml}
                  />
                </StackItem>
              );
            }
            return (
              <StackItem key={index} display='block'>
                {mapper(type) && React.createElement(mapper(type) as any, { model: element, pageBodyLinks })}
              </StackItem>
            );
          })}
          {Array.isArray(fixedNavModules) &&
            fixedNavModules
              .map((module, i) => {
                const fixedNavTxtModule = module.elements?.navigationBlock?.linkedItems[0];
                const fixedNavKey = module.elements?.navigationBlock?.value[0];
                const segmentFilter = module.elements?.taxonomySegment?.value[0];
                const pageSegmentCodenameArr = content.elements.tagsSegment?.value?.map((tags) => tags.codename) ?? [];
                const isFixedNavDisabled =
                  content.elements.disableAutoFixedNavigation?.value[0]?.codename == 'yes' ? true : false;
                const isInSegmentFilter = segmentFilter
                  ? pageSegmentCodenameArr.includes(segmentFilter.codename)
                  : false;
                if (fixedNavKey && fixedNavTxtModule && isInSegmentFilter && !isFixedNavDisabled) {
                  return {
                    fixedNavKey: `${fixedNavKey}_${i}`,
                    fixedNavTxtModule,
                  };
                }
                return null;
              })
              .filter((rawItm) => rawItm != null)
              .map((item, i) => (
                <StackItem key={item ? item.fixedNavKey : `fixedNav-${i}`} display='block'>
                  {item && item.fixedNavTxtModule && <TextModule model={item.fixedNavTxtModule} enMarginTop={true} />}
                </StackItem>
              ))}
        </VStack>
      )}
    </>
  );
};
